import { DeepPartial, LineStyleOptions, PriceFormat, PriceScaleOptions, SeriesOptionsCommon } from 'lightweight-charts';

import { isTabletOrMobileDevice } from './app';

export const MARKET_GRAPH_TABLET_MOBILE_WIDTH = 768;

export const ChartSelections = {
  odds: 'odds',
  chance: 'chance'
} as const;

export const MarketGraphDataStatuses = {
  pending: 'PENDING',
  noData: 'NO_DATA',
  ready: 'READY',
  updated: 'UPDATED'
} as const;

export const MARKET_GRAPH_SELECTION_PARAM_NAME = 'selection';
export const MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME = 'filter';

const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
export const ONE_HOUR_IN_MILLISECONDS = ONE_MINUTE_IN_MILLISECONDS * 60;
export const INTERVAL_15_SEC = 15 * 1000; // 15 seconds in milliseconds
export const INTERVAL_5_MIN = ONE_MINUTE_IN_MILLISECONDS * 5; // 5 minutes in milliseconds
export const INTERVAL_2_HOURS = ONE_HOUR_IN_MILLISECONDS * 2; // 2 hours in milliseconds
export const INTERVAL_1_DAY = ONE_MINUTE_IN_MILLISECONDS * 60 * 24; // 1 day in milliseconds
export const ONE_MONTH_IN_MILLISECONDS = INTERVAL_1_DAY * 31; // 1 month in milliseconds

export const DESKTOP_STATIC_ELEMENTS_HEIGHT = 210; // sum of all static elements heights (margins, paddings and gaps are included) above and under chart on desktop
export const MIN_DESKTOP_WINDOW_HEIGHT = 600;

// Grouping all data from BE to make linear data (with whitespaces) by following groups
export const MarketGraphChartDataGroupingPeriods = {
  sec15: 'sec15', // 15 seconds - from 10 min to 1 hour (including)
  min5: 'min5', // 5 minutes - from 1 hour to 1 day (including)
  hour2: 'hour2', // 2 hours - from 1 day to 1 month (including)
  day1: 'day1' // 1 day -  from 1+ month
} as const;

export const BORDER_DARK_THEME_COLOR = '#666666';
export const BORDER_LIGHT_THEME_COLOR = '#dddddd';
export const TEXT_COLOR = '#999999';
export const BACKGROUND_DARK_THEME_COLOR = '#303030';
export const BACKGROUND_LIGHT_THEME_COLOR = '#ffffff';

export const PeriodsIntervalsMap = {
  [MarketGraphChartDataGroupingPeriods.sec15]: INTERVAL_15_SEC,
  [MarketGraphChartDataGroupingPeriods.min5]: INTERVAL_5_MIN,
  [MarketGraphChartDataGroupingPeriods.hour2]: INTERVAL_2_HOURS,
  [MarketGraphChartDataGroupingPeriods.day1]: INTERVAL_1_DAY
};

export const IntervalsPeriodsMap = {
  [INTERVAL_15_SEC]: MarketGraphChartDataGroupingPeriods.sec15,
  [INTERVAL_5_MIN]: MarketGraphChartDataGroupingPeriods.min5,
  [INTERVAL_2_HOURS]: MarketGraphChartDataGroupingPeriods.hour2,
  [INTERVAL_1_DAY]: MarketGraphChartDataGroupingPeriods.day1
};

export const MarketGraphChartFilters = {
  all: 'all',
  day: 'day',
  hour: 'hour',
  custom: 'custom'
} as const;

export const LINE_SERIES_OPTIONS: DeepPartial<LineStyleOptions & SeriesOptionsCommon> = {
  priceScaleId: 'left',
  lineWidth: 1,
  lastValueVisible: false,
  priceLineVisible: false
};

export const LINE_SERIES_ODDS_PRICE_FORMAT: DeepPartial<PriceFormat> = {
  type: 'custom',
  minMove: 0.01,
  formatter: (price: number) => Number(price.toFixed(2))
};

export const LINE_SERIES_CHANCE_PRICE_FORMAT: DeepPartial<PriceFormat> = {
  type: 'custom',
  minMove: 0.01,
  formatter: (price: number) => {
    if (price > 100) {
      return '';
    }

    return Number(price.toFixed(2));
  }
};

export const PRICE_SCALE_OPTIONS: DeepPartial<PriceScaleOptions> = {
  scaleMargins: {
    top: 0.1, // highest point of the series will be 10% away from the top
    bottom: 0
  }
};

export const MarketDefinitionRunnerStatuses = {
  ACTIVE: 'ACTIVE',
  WINNER: 'WINNER',
  LOSER: 'LOSER',
  REMOVED_VACANT: 'REMOVED_VACANT',
  REMOVED: 'REMOVED',
  HIDDEN: 'HIDDEN',
  PLACED: 'PLACED'
} as const;

export const MARKET_GRAPH_WINDOW_NAME = 'market_graph_window';
export const MARKET_GRAPH_WINDOW_DETAILS_SESSION_STORAGE_NAME = 'market_graph_window_details';
export const MARKET_GRAPH_DROPDOWN_TAB_INDEX = 1;
export const MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX = MARKET_GRAPH_DROPDOWN_TAB_INDEX + 1;

export const MARKET_GRAPH_CHART_FILTERS = [
  { labelKey: 'marketGraph.labels.allTime', value: MarketGraphChartFilters.all },
  { labelKey: 'marketGraph.labels.24hours', value: MarketGraphChartFilters.day },
  { labelKey: 'marketGraph.labels.hour', value: MarketGraphChartFilters.hour }
];

export const TOOLTIP_WIDTH = 170;
export const TOOLTIP_HEIGHT = isTabletOrMobileDevice ? 78 : 70;
export const TOOLTIP_MARGIN = 34;

export const MARKET_GRAPH_ODDS_FILTER_LOCAL_STORAGE_NAME = 'market-graph-odds-filter';
export const MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME = 'market-graph-time-filter';
